import type { IProjectRepository } from "@/application/ports/repositories";
import type {
  PaginatedProjects,
  Project,
  ProjectListParams,
  CreateProjectResponse,
} from "@/domain";
import type { CreateProjectRequest } from "@/domain/project";
import type { DocumentDownloadResponse } from "@/domain/documentBlocks";
import { apiClient } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class ProjectRepository implements IProjectRepository {
  private basePath = "/projects";
  async createProject(
    data: CreateProjectRequest & { beneficiary_uuid: string },
  ): Promise<CreateProjectResponse> {
    const { beneficiary_uuid, ...projectData } = data;

    // Filter out empty strings to prevent backend validation errors
    const cleanedData = Object.fromEntries(
      Object.entries(projectData).filter((entry) => entry[1] !== ""),
    );

    const response = await apiClient.post<CreateProjectResponse>(
      `/beneficiaries/${beneficiary_uuid}/projects`,
      cleanedData,
      { headers: { "Content-Type": "application/json" } },
    );
    return response.data;
  }

  async getProjects(params?: ProjectListParams): Promise<{
    projects: Project[];
    total: number;
    total_unfiltered_count: number;
    page: number;
    total_pages: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.search) queryParams.append("search", params.search);
      if (params.beneficiary_uuid) queryParams.append("beneficiary_uuid", params.beneficiary_uuid);
      if (params.fiscal_quota_uuid)
        queryParams.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
      if (params.subtype) queryParams.append("subtype", params.subtype);
      if (params.status) queryParams.append("status", params.status);
      if (params.fiscal_year) queryParams.append("fiscal_year", params.fiscal_year);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_direction) queryParams.append("sort_direction", params.sort_direction);
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.offset !== undefined) queryParams.append("offset", params.offset.toString());
    }

    const url = queryParams.toString()
      ? `${this.basePath}?${queryParams.toString()}`
      : this.basePath;

    const response = await apiClient.get<PaginatedProjects>(url);

    const limit = params?.limit || 100;
    const offset = params?.offset || 0;
    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.ceil((response.data.total_count || 0) / limit);

    return {
      projects: response.data.projects,
      total: response.data.total_count || response.data.projects.length,
      total_unfiltered_count:
        response.data.total_unfiltered_count ??
        (response.data.total_count || response.data.projects.length),
      page: currentPage,
      total_pages: totalPages,
    };
  }

  async getProject(id: string): Promise<Project> {
    const response = await apiClient.get<Project>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getBeneficiaryProjects(beneficiaryUuid: string): Promise<Project[]> {
    const response = await apiClient.get<Project[]>(`/beneficiaries/${beneficiaryUuid}/projects`);
    return response.data;
  }

  async updateProject(
    id: string,
    data: Partial<CreateProjectRequest>,
  ): Promise<CreateProjectResponse> {
    const response = await apiClient.patch<CreateProjectResponse>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  async archiveProject(id: string): Promise<CreateProjectResponse> {
    const response = await apiClient.patch<CreateProjectResponse>(`${this.basePath}/${id}`, {
      status: "inactive",
    });
    return response.data;
  }

  async unarchiveProject(id: string): Promise<CreateProjectResponse> {
    const response = await apiClient.patch<CreateProjectResponse>(`${this.basePath}/${id}`, {
      status: "active",
    });
    return response.data;
  }

  async getPosterInfo(projectUuid: string): Promise<DocumentDownloadResponse> {
    const response = await apiClient.get<DocumentDownloadResponse>(
      `/projects/${projectUuid}/poster`,
    );
    return response.data;
  }

  async uploadPoster(projectUuid: string, file: File): Promise<void> {
    const logger = getLogger();
    try {
      const formData = new FormData();
      formData.append("poster_file", file);
      await apiClient.post(`/projects/${projectUuid}/poster`, formData);
    } catch (error) {
      logger.error("Error uploading poster:", error);
      throw error;
    }
  }

  async deletePoster(projectUuid: string): Promise<void> {
    await apiClient.delete(`/projects/${projectUuid}/poster`);
  }
}
